import { z } from 'zod';

export const AP_DOCUMENT_CLASSES = [
  'vendor_invoice',
  'vendor_credit',
  'vendor_statement',
  'order_acknowledgment',
  'shipment_tracking',
  'partial_backorder_change',
  'delivery_receipt_evidence',
  'customer_po_or_misroute',
  'payment_remittance_inquiry',
  'bank_detail_change_request',
  'noise',
  'unclassified',
] as const;

export type ApDocumentClass = (typeof AP_DOCUMENT_CLASSES)[number];

export const AP_EXCEPTION_TYPES = [
  'unclassified',
  'waiting_scan',
  'missing_po',
  'uncertain_match',
  'possible_duplicate',
  'possible_revision',
  'amount_discrepancy',
  'quantity_discrepancy',
  'multiple_tracking_numbers',
  'sensitive_request',
  'misroute',
  'provider_failure',
  // MUL-4205 distributor auto-file lane: the lane evaluated this item and held
  // it (or, in dry-run, records what it would have done). The specific failed
  // checks live in the exception detail.
  'auto_file_hold',
] as const;

export type ApExceptionType = (typeof AP_EXCEPTION_TYPES)[number];

export const AP_CANDIDATE_FIELDS = [
  'vendor_name',
  'document_number',
  'invoice_number',
  'credit_number',
  'po_number',
  'vendor_order_number',
  'tracking_number',
  'document_date',
  'ship_date',
  'expected_arrival_date',
  'amount',
  'currency',
  'quantity',
  'line_description',
] as const;

export type ApCandidateField = (typeof AP_CANDIDATE_FIELDS)[number];

export const apEvidenceCandidateSchema = z.object({
  field: z.enum(AP_CANDIDATE_FIELDS),
  value: z.string().trim().min(1).max(500),
  normalizedValue: z.string().trim().min(1).max(500),
  confidence: z.number().min(0).max(1),
  sourceKind: z.enum(['subject', 'sender', 'attachment_name', 'mime_part', 'document']),
  sourceRef: z.string().trim().min(1).max(500),
  startOffset: z.number().int().min(0).nullable(),
  endOffset: z.number().int().min(0).nullable(),
  excerpt: z.string().max(500),
});

export const apClassificationProposalSchema = z.object({
  documentClass: z.enum(AP_DOCUMENT_CLASSES),
  confidence: z.number().min(0).max(1),
  reasonCodes: z.array(z.string().trim().min(1).max(100)).max(20),
  sensitive: z.boolean(),
  requiresReview: z.boolean(),
  candidates: z.array(apEvidenceCandidateSchema).max(200),
  exceptions: z.array(z.enum(AP_EXCEPTION_TYPES)).max(AP_EXCEPTION_TYPES.length),
});

export type ApEvidenceCandidateProposal = z.infer<typeof apEvidenceCandidateSchema>;
export type ApClassificationProposal = z.infer<typeof apClassificationProposalSchema>;

export interface ApMetadataClassificationInput {
  subject?: string | null;
  senderName?: string | null;
  senderAddress?: string | null;
  attachmentNames: Array<{ id: string; name: string }>;
  allAttachmentsClean: boolean;
}

interface Rule {
  documentClass: ApDocumentClass;
  confidence: number;
  reasonCode: string;
  pattern: RegExp;
  sensitive?: boolean;
  misroute?: boolean;
}

// Ordered from the highest-risk/specific classes to broad invoice/noise rules.
const RULES: Rule[] = [
  {
    documentClass: 'bank_detail_change_request',
    confidence: 0.99,
    reasonCode: 'bank-detail-language',
    sensitive: true,
    pattern:
      /\b(bank|banking|remit(?:tance)?|wire|ach)\b.{0,40}\b(change|update|new|replacement|details?|instructions?)\b|\b(change|update|new)\b.{0,40}\b(bank|routing|account|ach|wire)\b/i,
  },
  {
    documentClass: 'vendor_credit',
    confidence: 0.96,
    reasonCode: 'credit-memo-language',
    pattern: /\bcredit\s*(memo|note)?\b|\bvendor\s+credit\b/i,
  },
  {
    documentClass: 'vendor_statement',
    confidence: 0.94,
    reasonCode: 'statement-language',
    pattern: /\b(statement|account\s+summary|aging\s+report)\b/i,
  },
  {
    documentClass: 'partial_backorder_change',
    confidence: 0.95,
    reasonCode: 'backorder-language',
    pattern:
      /\b(back[- ]?order(?:ed)?|partial\s+ship(?:ment)?|date\s+change|delayed|allocation|order\s+exception|unable\s+to\s+fulfill|order\s+cancel(?:led|ed))\b/i,
  },
  {
    documentClass: 'delivery_receipt_evidence',
    confidence: 0.94,
    reasonCode: 'delivery-language',
    pattern:
      /\b(proof\s+of\s+delivery|delivered|delivery\s+(confirmation|receipt)|received\s+by)\b/i,
  },
  {
    documentClass: 'shipment_tracking',
    confidence: 0.93,
    reasonCode: 'shipment-language',
    pattern: /\b(shipped|shipment|tracking|packing\s*(slip|list)|carrier|waybill)\b/i,
  },
  {
    documentClass: 'order_acknowledgment',
    confidence: 0.92,
    reasonCode: 'order-ack-language',
    pattern: /\b(order\s+(acknowledg(e)?ment|confirmation)|sales\s+order|order\s+accepted)\b/i,
  },
  {
    documentClass: 'payment_remittance_inquiry',
    confidence: 0.9,
    reasonCode: 'payment-inquiry-language',
    pattern:
      /\b(payment\s+(status|confirmation|received|processed|inquiry)|when\s+will.{0,20}paid|remittance\s+(advice|status)|past\s+due|paid\s+invoice)\b/i,
  },
  {
    documentClass: 'customer_po_or_misroute',
    confidence: 0.88,
    reasonCode: 'customer-po-language',
    misroute: true,
    pattern:
      /\b(customer\s+(po|purchase\s+order)|bill\s+to\s+customer|customer\s+remittance|accounts?\s+receivable)\b/i,
  },
  {
    documentClass: 'vendor_invoice',
    confidence: 0.88,
    reasonCode: 'invoice-language',
    pattern: /\b(invoice|tax\s+invoice|commercial\s+invoice)\b/i,
  },
  {
    documentClass: 'noise',
    confidence: 0.86,
    reasonCode: 'noise-language',
    pattern:
      /\b(out\s+of\s+office|automatic\s+reply|read\s+receipt|delivery\s+status\s+notification|unsubscribe|newsletter|marketing)\b/i,
  },
];

function sourceCandidate(
  field: ApCandidateField,
  value: string,
  confidence: number,
  sourceKind: ApEvidenceCandidateProposal['sourceKind'],
  sourceRef: string,
  sourceText: string,
  startOffset: number,
): ApEvidenceCandidateProposal {
  return {
    field,
    value,
    normalizedValue: value.trim().toUpperCase(),
    confidence,
    sourceKind,
    sourceRef,
    startOffset,
    endOffset: startOffset + value.length,
    excerpt: sourceText.slice(
      Math.max(0, startOffset - 40),
      Math.min(sourceText.length, startOffset + value.length + 40),
    ),
  };
}

function extractFromText(
  text: string,
  sourceKind: ApEvidenceCandidateProposal['sourceKind'],
  sourceRef: string,
): ApEvidenceCandidateProposal[] {
  const candidates: ApEvidenceCandidateProposal[] = [];
  const patterns: Array<{
    field: ApCandidateField;
    confidence: number;
    regex: RegExp;
    group: number;
  }> = [
    {
      field: 'po_number',
      confidence: 0.88,
      regex: /\b(?:P\.?O\.?|purchase\s+order)\s*(?:number|no\.?|#|:)\s*([A-Z0-9][A-Z0-9-]{2,30})/gi,
      group: 1,
    },
    {
      field: 'invoice_number',
      confidence: 0.88,
      regex: /\b(?:invoice|inv)\s*(?:number|no\.?|#|:)\s*([A-Z0-9][A-Z0-9-]{1,40})/gi,
      group: 1,
    },
    {
      field: 'vendor_order_number',
      confidence: 0.85,
      regex: /\b(?:sales|vendor)\s+order\s*(?:number|no\.?|#|:)\s*([A-Z0-9][A-Z0-9-]{1,40})/gi,
      group: 1,
    },
    // UPS 1Z values are carrier-specific. Numeric values are accepted only
    // beside an explicit carrier/tracking label so invoice, account, order,
    // pixel, and other long numbers cannot become shipment evidence.
    { field: 'tracking_number', confidence: 0.93, regex: /\b(1Z[A-Z0-9]{16})\b/gi, group: 1 },
    {
      field: 'tracking_number',
      confidence: 0.91,
      regex:
        /\b(?:tracking(?:\s*(?:number|no\.?|#))?|waybill|fedex(?:\s+tracking)?|ups(?:\s+tracking)?|usps(?:\s+tracking)?)\s*(?:is|:|#|-)?\s*(\d{12,22})\b/gi,
      group: 1,
    },
    {
      field: 'amount',
      confidence: 0.72,
      regex: /(?:USD\s*)?\$\s*([0-9]{1,3}(?:,[0-9]{3})*(?:\.\d{2})|[0-9]+\.\d{2})\b/g,
      group: 1,
    },
  ];
  for (const pattern of patterns) {
    for (const match of text.matchAll(pattern.regex)) {
      const value = match[pattern.group];
      if (!value || match.index === undefined) continue;
      const relative = match[0].indexOf(value);
      candidates.push(
        sourceCandidate(
          pattern.field,
          value,
          pattern.confidence,
          sourceKind,
          sourceRef,
          text,
          match.index + relative,
        ),
      );
    }
  }
  return candidates;
}

function dedupeCandidates(
  candidates: ApEvidenceCandidateProposal[],
): ApEvidenceCandidateProposal[] {
  const seen = new Set<string>();
  return candidates.filter((candidate) => {
    const key =
      candidate.field === 'tracking_number'
        ? `${candidate.field}:${candidate.normalizedValue}`
        : `${candidate.field}:${candidate.normalizedValue}:${candidate.sourceKind}:${candidate.sourceRef}:${candidate.startOffset}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function classifyApMetadata(input: ApMetadataClassificationInput): ApClassificationProposal {
  const subject = (input.subject ?? '').slice(0, 1000);
  const attachmentText = input.attachmentNames
    .map((attachment) => attachment.name.slice(0, 500))
    .join(' ');
  // Prefer an explicit subject classification over a secondary attachment-name
  // hint (for example an invoice email containing a tracking-number PDF).
  const rule =
    RULES.find((candidate) => candidate.pattern.test(subject)) ??
    RULES.find((candidate) => candidate.pattern.test(attachmentText));
  const documentClass = rule?.documentClass ?? 'unclassified';
  const confidence = rule?.confidence ?? 0;
  const exceptions = new Set<ApExceptionType>();
  if (!rule) exceptions.add('unclassified');
  if (!input.allAttachmentsClean && input.attachmentNames.length > 0)
    exceptions.add('waiting_scan');
  if (rule?.sensitive) exceptions.add('sensitive_request');
  if (rule?.misroute) exceptions.add('misroute');

  const candidates = dedupeCandidates([
    ...extractFromText(subject, 'subject', 'subject'),
    ...input.attachmentNames.flatMap((attachment) =>
      extractFromText(attachment.name.slice(0, 500), 'attachment_name', attachment.id),
    ),
  ]);
  const trackingNumbers = new Set(
    candidates
      .filter((candidate) => candidate.field === 'tracking_number')
      .map((candidate) => candidate.normalizedValue),
  );
  const hasPo = candidates.some((candidate) => candidate.field === 'po_number');
  if (['vendor_invoice', 'vendor_credit'].includes(documentClass) && !hasPo)
    exceptions.add('missing_po');
  if (confidence > 0 && confidence < 0.9) exceptions.add('uncertain_match');
  if (trackingNumbers.size > 1) exceptions.add('multiple_tracking_numbers');

  return apClassificationProposalSchema.parse({
    documentClass,
    confidence,
    reasonCodes: [
      rule?.reasonCode ?? 'no-metadata-rule-match',
      ...(trackingNumbers.size > 1 ? ['multiple-tracking-values'] : []),
    ],
    sensitive: rule?.sensitive ?? false,
    requiresReview: documentClass !== 'noise' || exceptions.size > 0,
    candidates,
    exceptions: [...exceptions],
  });
}
